'use client';

import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import { useRouter } from '@/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { Icon } from '@/components/icons/svg-icons';
import { AutoPopupModal } from '@/components/widgets/modal';
import { Button } from '@nf/ui';
import { WcPromotionButton } from '@/features/wc-promotion-2026/components/wc-promotion-button';
import { useWcPromotionAllPools } from '@/features/wc-promotion-2026/hook/use-wc-promotion-all-pools';
import { useWcPromotionWinningNotify } from '@/features/wc-promotion-2026/hook/use-wc-promotion-winning-notify';
import { convertCultureToRef } from '@/utils/common-util';
import styles from './wc-promotion-prize-modal.module.scss';
import { KafkaNote } from '@nf/utils-common/constants';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { AutoPopupKey } from '@nf/auto-popup-handler';

type PrizeModalType = 'weeklyLottery' | 'afterParty';

const supportLanguages = ['ch', 'cs', 'zhcn', 'jp', 'ko', 'th', 'id', 'beng'];

const PRIZE_MODAL_ASSETS: Record<PrizeModalType, { filenameBase: string; alt: string }> = {
	weeklyLottery: {
		filenameBase: 'weekly-lottery',
		alt: 'Weekly lottery'
	},
	afterParty: {
		filenameBase: 'after-party',
		alt: 'After party'
	}
};

export const WcPromotionPrizeModal = () => {
	const t = useTranslations('General');
	const locale = useLocale();
	const router = useRouter();
	const { data: allPools } = useWcPromotionAllPools(); // 取得所有獎池清單
	const { data: winningNotifyData, setWcPromotionWinningNotify } = useWcPromotionWinningNotify(); // 查詢該使用者是否有中獎 & 標記使用者已接收中獎通知
	const [isPrizeModalOpen, setIsPrizeModalOpen] = useState(false);
	const [hasCheckedNotify, setHasCheckedNotify] = useState(false);
	const afterPartyPoolId = useMemo(() => allPools.length > 1 ? allPools[allPools.length - 1]?.PoolId : '', [allPools]);
	const hasWinningNotify = useMemo(() => winningNotifyData?.PoolIds ?? [], [winningNotifyData]);
	const hasAfterPartyWin = useMemo(() => afterPartyPoolId && hasWinningNotify?.includes(afterPartyPoolId), [afterPartyPoolId, hasWinningNotify]);
	const prizeModalType = hasAfterPartyWin ? 'afterParty' : 'weeklyLottery';
	const shortLang = convertCultureToRef(locale ?? 'en-US', true);
	const lang = supportLanguages.includes(shortLang) ? shortLang === 'zhcn' ? 'cs' : shortLang : 'en';
	const { filenameBase, alt: imageAlt } = useMemo(() => PRIZE_MODAL_ASSETS[prizeModalType], [prizeModalType]);

	useEffect(() => {
		if (winningNotifyData?.PoolIds?.length && !hasCheckedNotify) {
			setIsPrizeModalOpen(true);
			setHasCheckedNotify(true);
		}
	}, [hasCheckedNotify, winningNotifyData?.PoolIds?.length]);


	const handleActionClick = () => {
		setIsPrizeModalOpen(false);
		setWcPromotionWinningNotify();
		const winningPoolIds = winningNotifyData?.PoolIds ?? [];
		if (!winningPoolIds.length) return;

		if (hasAfterPartyWin) {
			setKafkaNormal({
				note: KafkaNote.WcPromotion2026,
				type: 1,
				action: 37
			});
			router.push('/wc-promotion?tab=party');
			return;
		}

		const targetPoolId = winningPoolIds[winningPoolIds.length - 1];
		if (targetPoolId) {
			setKafkaNormal({
				note: KafkaNote.WcPromotion2026,
				type: 1,
				action: 34
			});
			router.push(`/wc-promotion?tab=lottery&poolId=${encodeURIComponent(targetPoolId)}`);
		}
	};

	return winningNotifyData?.PoolIds?.length ? (
		<AutoPopupModal
			popupKey={AutoPopupKey.WcPromotionPrize}
			isOpen={isPrizeModalOpen}
			variant="event"
			closeModal={() => setIsPrizeModalOpen(false)}
			showHeader={false}
			className={styles.promoModal}
			isShowClose={true}
			isBackdropClose={false}
		>
			<div className={styles.container}>
				<Button
					variant="icon"
					className={clsx(styles.closeButton)}
					onClick={() => setIsPrizeModalOpen(false)}
				>
					<Icon name="close-round-line" />
				</Button>
				<div className={styles.prize}>
					<Image src={ `/images/promotion/worldcup2026/prize-modal/${filenameBase}-${lang}.png`} alt={imageAlt} width={310} height={240} />
					<div className={styles.description}>
						{t('lbl_26WCProWinningResult03')}<br />
						{hasAfterPartyWin ? t('lbl_26WCProWinningResult02') : t('lbl_26WCProWinningResult01')}
					</div>
					<WcPromotionButton
						label={t('lbl_CheckResult')}
						iconName="search"
						variant="primary"
						onClick={handleActionClick}
						className={styles.button}
					/>
				</div>
			</div>
		</AutoPopupModal>
	) : null;
};